import {DotIcon} from '@primer/octicons-react'
import {BranchName, NavList} from '@primer/react'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'

import styles from '../stack-section/StackList.module.css'
import iconStyles from '../Icon.module.css'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {RebaseState, type StackRebasePullRequestEntry} from '../../../hooks/mutations/use-stack-rebase-mutation'
import {RebasingStackEntryNavItem} from './RebasingStackEntryNavItem'
import {getRebaseStateBaseRefLineClass} from './helpers/rebase-state-line-class'

// Resolves the `RebaseState` for a stack entry from the live rebase status.
// When the rebase request itself failed (`rebaseFailed`), any entry that was
// still "Rebasing" never got a result, so it is surfaced as `Skipped` rather
// than a spinner that would otherwise hang forever.
function rebaseStateForEntry(
  entry: StackSectionStackEntry,
  rebaseStatus: Record<number, StackRebasePullRequestEntry>,
  rebaseFailed: boolean,
): RebaseState | undefined {
  const status = rebaseStatus[entry.pull.number]
  const state = status?.state ?? (entry.pull.state === 'OPEN' ? RebaseState.Rebasing : undefined)
  if (rebaseFailed && state === RebaseState.Rebasing) return RebaseState.Skipped
  return state
}

/**
 * Renders the list of stacked pull requests and their rebase status while a
 * stack rebase is in progress. Modeled after `StackList`, but each entry's
 * status/icon/label is derived from the live rebase status (`RebaseState`)
 * rather than the merge-requirements-derived `effectiveEntryStatus`.
 */
export function RebasingStacksList({
  baseRefName,
  stackEntries,
  rebaseStatus,
  rebaseFailed = false,
}: {
  baseRefName?: string
  stackEntries: StackSectionStackEntry[]
  rebaseStatus: Record<number, StackRebasePullRequestEntry>
  rebaseFailed?: boolean
}) {
  const {pathname} = useLocation()
  const tabSuffix = getPullRequestTabSuffix(pathname)

  const entryStates = stackEntries.map(entry => rebaseStateForEntry(entry, rebaseStatus, rebaseFailed))

  return (
    <NavList className={styles.stackNavList} aria-label="Rebasing stacked pull requests list">
      {stackEntries.map((entry, idx) => (
        <RebasingStackEntryNavItem
          key={entry.pull.number}
          entry={entry}
          rebaseState={entryStates[idx]}
          previousRebaseState={idx > 0 ? entryStates[idx - 1] : undefined}
          tabSuffix={tabSuffix}
        />
      ))}
      {baseRefName && stackEntries.length > 0 && (
        <li className={`${styles.branchItem} ${getRebaseStateBaseRefLineClass(entryStates)}`}>
          <span className={styles.branchItemContent}>
            <DotIcon size={16} className={`color-fg-muted ${iconStyles.alignedBaseRefDot}`} />
            <BranchName as="span">{baseRefName}</BranchName>
          </span>
        </li>
      )}
    </NavList>
  )
}
